import { DOCUMENT, registerLocaleData } from '@angular/common';
import { inject, Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { registerLocale } from 'i18n-iso-countries';
import moment from 'moment';

import { DEFAULT_LANG, LOCALES } from './i18n.config';
import { ILocale, ILocaleShort } from './i18n.types';
import { WhiteLabelConfigService } from '../white-label/white-label-config.service';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private readonly LOCALIZATION_LOCAL_STORAGE_KEY = 'language';
  private langToLocaleIdMap: Record<string, string> = {};
  private readonly whiteLabelConfigService: WhiteLabelConfigService;

  constructor(
    @Inject(LOCALES) private locales: ILocale[],
    @Inject(DOCUMENT) private document: Document,
    private translate: TranslateService,
    @Inject(DEFAULT_LANG) defaultLang: string,
  ) {
    this.whiteLabelConfigService = inject(WhiteLabelConfigService);
    this.translate.setDefaultLang(defaultLang);
  }

  public get selectedLang(): string {
    return (
      localStorage.getItem(this.LOCALIZATION_LOCAL_STORAGE_KEY) ||
      this.translate.getDefaultLang()
    );
  }

  public get selectedLocale(): ILocaleShort | null {
    const selectedLocale = this.selectedLocaleFull;
    return selectedLocale ? I18nService.localeToShort(selectedLocale) : null;
  }

  public get selectedLocaleFull(): ILocale | null {
    const selectedLocale =  this.locales.find(locale => locale.lang === this.selectedLang);
    return selectedLocale || null;
  }

  public get localesShortList(): ILocaleShort[] {
    return this.locales.map(I18nService.localeToShort);
  }

  private static localeToShort({ localeId, lang, name, flag }: ILocale): ILocaleShort {
    return { localeId, lang, name, flag };
  }

  public init = (): void => {
    const langIds: string[] = [];

    this.locales.forEach(locale => {
      langIds.push(locale.lang);
      this.langToLocaleIdMap[locale.lang] = locale.localeId;

      registerLocaleData(locale.localeData);
      this.translate.setTranslation(locale.lang, locale.vocabulary, true);

      if (this.whiteLabelConfigService.languageTranslations?.[locale.lang]) {
        this.translate.setTranslation(locale.lang, this.whiteLabelConfigService.languageTranslations[locale.lang], true);
      }

      registerLocale({
        locale: locale.lang,
        countries: locale.countries,
      });
    });

    this.translate.addLangs(langIds);
    this.translate.use(this.selectedLang);
    moment.locale(this.selectedLang);
  }

  public setLanguage(lang: string) {
    if (this.translate.getLangs().includes(lang)) {
      localStorage.setItem(this.LOCALIZATION_LOCAL_STORAGE_KEY, lang);
      /**
       * The only option to really switch locale is to re-load page.
       * You can try to find some other option but not forget about switching
       * locale in moment.
       *
       * this.translate.use(this.selectedLang);
       * moment.locale(this.selectedLang);
       */
      this.document.location.reload();
    } else {
      // eslint-disable-next-line no-console
      console.error(`There is not such language in the list: ${lang}`);
    }
  }

  public getSelectedLocaleId(): string {
    return this.langToLocaleIdMap[this.selectedLang];
  }
}
